import {
  AccessDeniedError,
  backButton,
  closingBehavior,
  cloudStorage,
  hapticFeedback,
  init as initSDK,
  mainButton,
  miniApp,
  openLink,
  openTelegramLink,
  requestContactComplete,
  retrieveLaunchParams,
  retrieveRawInitData,
  setDebug,
  swipeBehavior,
  themeParams,
  useSignal,
  viewport,
} from '@tma.js/sdk-react';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { isMockedEnv } from '@/mockEnv';

declare global {
  interface Window {
    Capacitor?: { isNativePlatform?: () => boolean };
  }
}

export const IS_NATIVE = window.Capacitor?.isNativePlatform?.() === true;

const native = () => import('@/platform/native');

const DESKTOP = new Set(['tdesktop', 'macos', 'unigram']);

export function fillDesktopScreen(platform: string): void {
  if (!DESKTOP.has(platform) || viewport.isFullscreen()) return;
  const asked = viewport.requestFullscreen.ifAvailable();
  if (asked.ok) asked.data.catch(() => {});
}

function initTelegram(): void {
  const { tgWebAppStartParam, tgWebAppPlatform } = retrieveLaunchParams();
  setDebug((tgWebAppStartParam || '').includes('debug') || import.meta.env.DEV);
  initSDK();

  mainButton.mount.ifAvailable();
  backButton.mount.ifAvailable();
  closingBehavior.mount.ifAvailable();
  swipeBehavior.mount.ifAvailable();
  swipeBehavior.disableVertical.ifAvailable();

  if (miniApp.mount.isAvailable()) {
    themeParams.mount();
    miniApp.mount();
    themeParams.bindCssVars();
    miniApp.ready.ifAvailable();
  }
  if (viewport.mount.isAvailable()) {
    void viewport.mount().then(() => {
      viewport.bindCssVars();
      viewport.expand.ifAvailable();
      fillDesktopScreen(tgWebAppPlatform);
    });
  }
}

const CHROME = {
  dark: { header: '#17191d', bg: '#141619' },
  light: { header: '#dcd9d2', bg: '#e9e7e2' },
} as const;

export function applyTelegramChrome(theme: 'dark' | 'light'): void {
  if (IS_NATIVE || !miniApp.isMounted()) return;
  const c = CHROME[theme];
  miniApp.setHeaderColor.ifAvailable(c.header);
  miniApp.setBgColor.ifAvailable(c.bg);
  miniApp.setBottomBarColor.ifAvailable(c.header);
}

export function useClosingConfirmation(active: boolean): void {
  useEffect(() => {
    if (IS_NATIVE || !active) return;
    closingBehavior.enableConfirmation.ifAvailable();
    return () => {
      closingBehavior.disableConfirmation.ifAvailable();
    };
  }, [active]);
}

export function useTelegramBackButton(visible: boolean, onClick: () => void): void {
  const onClickRef = useRef(onClick);
  useEffect(() => {
    onClickRef.current = onClick;
  });
  useEffect(() => {
    if (IS_NATIVE) return;
    const off = backButton.onClick.ifAvailable(() => onClickRef.current());
    return () => {
      if (off.ok) off.data();
    };
  }, []);
  useEffect(() => {
    if (IS_NATIVE) {
      nativeBack = () => {
        if (!visible) return false;
        onClickRef.current();
        return true;
      };
      return () => {
        nativeBack = null;
      };
    }
    if (visible) backButton.show.ifAvailable();
    else backButton.hide.ifAvailable();
  }, [visible]);
}

export function closeMiniApp(): boolean {
  if (IS_NATIVE) return false;
  return miniApp.close.ifAvailable().ok;
}

const isHome = () => ['', '#', '#/'].includes(window.location.hash);

export function closeOpenDialog(): boolean {
  const dialog = document.querySelector('[role$="dialog"][data-state="open"]');
  if (!dialog) return false;
  dialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  return true;
}

let nativeBack: (() => boolean) | null = null;

function initNative(): void {
  document.documentElement.classList.add('native');
  void native().then(({ App }) =>
    App.addListener('backButton', ({ canGoBack }) => {
      if (nativeBack?.()) return;
      if (closeOpenDialog()) return;
      if (isHome()) void App.exitApp();
      else if (canGoBack) window.history.back();
      else window.location.replace('#/');
    }),
  );
}

export function launch(): boolean {
  if (IS_NATIVE) {
    initNative();
    return true;
  }
  try {
    initTelegram();
    return true;
  } catch {
    return false;
  }
}

export function telegramInitData(): string | undefined {
  return IS_NATIVE ? undefined : retrieveRawInitData();
}

interface LaunchUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  language_code?: string;
}

export function launchUser(): LaunchUser | undefined {
  if (IS_NATIVE) return undefined;
  try {
    return retrieveLaunchParams().tgWebAppData?.user;
  } catch {
    return undefined;
  }
}

export function launchLanguage(): string | undefined {
  return IS_NATIVE ? navigator.language : launchUser()?.language_code;
}

export function handoffFrom(url: string | undefined): string | null {
  try {
    if (!url) return null;
    const u = new URL(url);
    return u.searchParams.get('t') ?? new URLSearchParams(u.hash.slice(1)).get('t');
  } catch {
    return null;
  }
}

const USED_HANDOFF = 'used_handoff';

export function onLoginLink(handle: (handoff: string) => void): () => void {
  if (!IS_NATIVE) return () => {};
  let active = true;
  const deliver = (url: string | undefined) => {
    const handoff = handoffFrom(url);
    if (!active || !handoff || sessionStorage.getItem(USED_HANDOFF) === handoff) return;
    sessionStorage.setItem(USED_HANDOFF, handoff);
    handle(handoff);
  };
  const listener = native().then(({ App }) => {
    void App.getLaunchUrl().then((launched) => deliver(launched?.url));
    return App.addListener('appUrlOpen', ({ url }) => deliver(url));
  });
  return () => {
    active = false;
    void listener.then((l) => l.remove());
  };
}

const TELEGRAM_HOSTS = ['t.me', 'telegram.me', 'telegram.dog'];

function hostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

export function openExternal(url: string): void {
  if (IS_NATIVE) {
    if (TELEGRAM_HOSTS.includes(hostname(url))) window.location.assign(url);
    else void native().then(({ Browser }) => Browser.open({ url }));
    return;
  }
  const opened = TELEGRAM_HOSTS.includes(hostname(url))
    ? openTelegramLink.ifAvailable(url)
    : openLink.ifAvailable(url);
  if (!opened.ok) window.open(url, '_blank', 'noopener');
}

export const haptic = {
  impact(): void {
    if (!IS_NATIVE) hapticFeedback.impactOccurred.ifAvailable('light');
  },
  selection(): void {
    if (!IS_NATIVE) hapticFeedback.selectionChanged.ifAvailable();
  },
  notify(type: 'success' | 'error'): void {
    if (!IS_NATIVE) hapticFeedback.notificationOccurred.ifAvailable(type);
    else if (type === 'error')
      void native().then(({ Haptics, NotificationType }) =>
        Haptics.notification({ type: NotificationType.Error }),
      );
  },
};

const darkQuery = () => window.matchMedia?.('(prefers-color-scheme: dark)');

function subscribeSystemDark(onChange: () => void): () => void {
  const query = darkQuery();
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

function useSystemDarkNative(): boolean {
  return useSyncExternalStore(subscribeSystemDark, () => darkQuery()?.matches ?? true);
}

function useSystemDarkTelegram(): boolean {
  return useSignal(themeParams.isDark);
}

export const useSystemDark = IS_NATIVE ? useSystemDarkNative : useSystemDarkTelegram;

export function applySystemBars(theme: 'dark' | 'light'): void {
  if (!IS_NATIVE) return;
  void native().then(({ SystemBars, SystemBarsStyle }) =>
    SystemBars.setStyle({
      style: theme === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
    }),
  );
}

export const cloud = {
  canRead: (): boolean => !IS_NATIVE && cloudStorage.getItem.isAvailable(),
  read: (key: string): Promise<unknown> => cloudStorage.getItem(key),
  write(key: string, raw: string): void {
    if (IS_NATIVE) return;
    const written = cloudStorage.setItem.ifAvailable(key, raw);
    if (written.ok) void written.data.catch(() => {});
  },
};

export const canRequestContact = (): boolean => !IS_NATIVE && requestContactComplete.isAvailable();

export const requestSignedContact = (timeout: number): Promise<string> =>
  requestContactComplete({ timeout }).then((shared) => shared.raw);

export const isAccessDenied = (e: unknown): boolean => AccessDeniedError.is(e);

export function useNativeMainButton({
  text,
  visible,
  waitingText,
  progress = false,
  light = false,
  onClick,
}: {
  text: string;
  visible: boolean;
  waitingText?: string;
  progress?: boolean;
  light?: boolean;
  onClick: () => void;
}): boolean {
  const hasNative = useSignal(mainButton.isMounted) && !isMockedEnv && !IS_NATIVE;

  const onClickRef = useRef(onClick);
  useEffect(() => {
    onClickRef.current = onClick;
  });

  useEffect(() => {
    if (!hasNative) return;
    if (!visible) {
      mainButton.setParams.ifAvailable(
        waitingText
          ? {
              isVisible: true,
              isEnabled: false,
              isLoaderVisible: progress,
              text: waitingText,
              bgColor: light ? '#dfdcd5' : '#23272e',
              textColor: light ? '#5d5b58' : '#a3a4a4',
            }
          : { isVisible: false },
      );
      return;
    }
    mainButton.setParams.ifAvailable({
      isVisible: true,
      isEnabled: true,
      isLoaderVisible: progress,
      text,
      bgColor: '#ffd400',
      textColor: '#16181c',
    });
    const off = mainButton.onClick.ifAvailable(() => onClickRef.current());
    return () => {
      if (off.ok) off.data();
    };
  }, [hasNative, visible, text, waitingText, progress, light]);

  useEffect(
    () => () => {
      if (!IS_NATIVE) mainButton.setParams.ifAvailable({ isVisible: false });
    },
    [],
  );

  return hasNative;
}
